import { GA4Config, GA4EventParams, GA4InitializationError } from './types';
import { DEFAULT_CONFIG, GA4_LIMITS } from './constants';

// Extend Window interface for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export class GA4Provider {
  private trackingId: string = '';
  private initialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private retryCount: number = 0;
  private debugMode: boolean = false;

  /**
   * Initialize GA4 with tracking ID and retry logic
   */
  async initialize(trackingId: string, debugMode: boolean = false): Promise<void> {
    if (!trackingId) {
      throw new GA4InitializationError(
        'Tracking ID is required',
        'MISSING_ID',
        false
      );
    }

    this.trackingId = trackingId;
    this.debugMode = debugMode;

    // Return existing initialization promise if already initializing
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.initializeWithRetry();
    return this.initializationPromise;
  }

  /**
   * Initialize with exponential backoff retry logic
   */
  private async initializeWithRetry(): Promise<void> {
    while (this.retryCount < DEFAULT_CONFIG.RETRY_ATTEMPTS) {
      try {
        await this.loadGtagScript();
        this.initializeGtag();
        this.initialized = true;
        return;
      } catch (error) {
        this.retryCount++;
        
        if (this.retryCount >= DEFAULT_CONFIG.RETRY_ATTEMPTS) {
          throw new GA4InitializationError(
            `Failed to initialize GA4 after ${DEFAULT_CONFIG.RETRY_ATTEMPTS} attempts`,
            'SCRIPT_LOAD_FAILED',
            false
          );
        }

        // Exponential backoff
        const delay = DEFAULT_CONFIG.RETRY_DELAY_MS * Math.pow(2, this.retryCount - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Load gtag.js script dynamically
   */
  private loadGtagScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector(
        `script[src*="googletagmanager.com/gtag/js"]`
      );
      
      if (existingScript) {
        if (this.debugMode) {
          console.log('[GA4Provider] gtag script already exists');
        }
        // Wait a bit for the script to execute
        setTimeout(() => resolve(), 100);
        return;
      }

      if (this.debugMode) {
        console.log('[GA4Provider] Loading gtag script...');
      }

      // Initialize dataLayer BEFORE loading the script
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]){window.dataLayer.push(args);}
      window.gtag = gtag as any;

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
      
      script.onload = () => {
        if (this.debugMode) {
          console.log('[GA4Provider] gtag script loaded successfully');
        }
        // Give the script time to execute and replace gtag
        setTimeout(() => resolve(), 100);
      };
      
      script.onerror = (error) => {
        console.error('[GA4Provider] Failed to load gtag script:', error);
        reject(new Error('Failed to load gtag.js script'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize gtag function and configure GA4
   */
  private initializeGtag(): void {
    // dataLayer and gtag are already initialized in loadGtagScript
    // Just configure GA4
    window.gtag('js', new Date());
    
    // Configure GA4 with debug mode support
    const config: Record<string, any> = {
      send_page_view: true, // Send automatic page views
      debug_mode: this.debugMode, // Enable GA4 DebugView in debug mode
    };
    
    // Force transport_url for localhost testing (GA4 sometimes blocks localhost)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      config['transport_url'] = 'https://www.google-analytics.com';
    }
    
    window.gtag('config', this.trackingId, config);
    
    // Explicitly send initial page_view event
    // This ensures the page view is tracked even if automatic tracking fails
    setTimeout(() => {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
    }, 500);
    
    if (this.debugMode) {
      console.log('[GA4Provider] gtag initialized with tracking ID:', this.trackingId);
      console.log('[GA4Provider] Debug mode enabled');
      console.log('[GA4Provider] dataLayer:', window.dataLayer);
    }
  }

  /**
   * Send event to GA4 with proper formatting
   */
  sendEvent(eventName: string, eventParams: GA4EventParams = {}): void {
    if (!this.initialized) {
      console.warn('[GA4Provider] Cannot send event - not initialized');
      return;
    }

    if (!window.gtag) {
      console.error('[GA4Provider] gtag function not available');
      return;
    }

    try {
      // Format event name to GA4 conventions
      const formattedEventName = this.formatEventName(eventName);
      
      // Format event parameters
      const formattedParams = this.formatEventParams(eventParams);
      
      if (this.debugMode) {
        console.log('[GA4Provider] Sending event:', formattedEventName, formattedParams);
      }
      
      // Send to GA4
      window.gtag('event', formattedEventName, formattedParams);
      
      if (this.debugMode) {
        console.log('[GA4Provider] Event sent successfully');
      }
    } catch (error) {
      console.error('[GA4Provider] Error sending event:', error);
      throw error;
    }
  }

  /**
   * Configure GA4 settings
   */
  configure(config: GA4Config): void {
    if (!this.initialized) {
      console.warn('[GA4Provider] Cannot configure - not initialized');
      return;
    }

    const configParams: Record<string, any> = {};

    if (config.anonymizeIp !== undefined) {
      configParams['anonymize_ip'] = config.anonymizeIp;
    }

    if (config.cookieFlags) {
      configParams['cookie_flags'] = config.cookieFlags;
    }

    if (config.customDimensions) {
      Object.assign(configParams, config.customDimensions);
    }

    if (config.customMetrics) {
      Object.assign(configParams, config.customMetrics);
    }

    window.gtag('config', this.trackingId, configParams);
  }

  /**
   * Set user properties in GA4
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.initialized) {
      console.warn('[GA4Provider] Cannot set user properties - not initialized');
      return;
    }

    window.gtag('set', 'user_properties', properties);
  }

  /**
   * Check if GA4 is loaded and ready
   */
  isReady(): boolean {
    return this.initialized && typeof window.gtag === 'function';
  }

  /**
   * Format event name to GA4 conventions
   * - Lowercase with underscores
   * - Max 40 characters
   */
  private formatEventName(eventName: string): string {
    let formatted = eventName
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');

    if (formatted.length > GA4_LIMITS.EVENT_NAME_MAX_LENGTH) {
      formatted = formatted.substring(0, GA4_LIMITS.EVENT_NAME_MAX_LENGTH);
      console.warn(
        `[GA4Provider] Event name truncated to ${GA4_LIMITS.EVENT_NAME_MAX_LENGTH} characters: ${formatted}`
      );
    }

    return formatted;
  }

  /**
   * Format event parameters to GA4 conventions
   */
  private formatEventParams(params: GA4EventParams): Record<string, any> {
    const formatted: Record<string, any> = {};

    for (const [key, value] of Object.entries(params)) {
      // Format parameter name
      let formattedKey = key
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

      if (formattedKey.length > GA4_LIMITS.PARAM_NAME_MAX_LENGTH) {
        formattedKey = formattedKey.substring(0, GA4_LIMITS.PARAM_NAME_MAX_LENGTH);
      }

      // Format parameter value
      let formattedValue = value;
      if (typeof value === 'string' && value.length > GA4_LIMITS.PARAM_VALUE_MAX_LENGTH) {
        formattedValue = value.substring(0, GA4_LIMITS.PARAM_VALUE_MAX_LENGTH);
        console.warn(
          `[GA4Provider] Parameter value truncated to ${GA4_LIMITS.PARAM_VALUE_MAX_LENGTH} characters: ${formattedKey}`
        );
      }

      formatted[formattedKey] = formattedValue;
    }

    return formatted;
  }

  /**
   * Reset initialization state (useful for testing)
   */
  reset(): void {
    this.initialized = false;
    this.initializationPromise = null;
    this.retryCount = 0;
  }
}
